import Button from '@atlaskit/button';
import { ErrorMessage } from '@atlaskit/form';
import CheckIcon from '@atlaskit/icon/glyph/check';
import CrossIcon from '@atlaskit/icon/glyph/cross';
import ErrorIcon from '@atlaskit/icon/glyph/error';
import Textfield from '@atlaskit/textfield';
import { fontSize, gridSize } from '../../../atlaskit.colors';
import Tooltip from '@atlaskit/tooltip';
import React, { ChangeEvent, useContext, useEffect, useRef, useState } from 'react';
import styled from '@emotion/styled';
import { GlobalContext } from '../../common/GlobalContext';
import { IdAndValue } from '../../../model/model';
import { OptionValidationErrorForView, FieldEditorActionReturnType } from '../../stateful/field-editor-stateful/FieldEditorStateful';
import { OptionSearchMatchMarker } from './search/custom-field-options-search-logic';
import { FieldEditorHighlightedOptionSearchMatch } from './search/FieldEditorHighlightedOptionSearchMatch';
import { DTOCustomFieldOption } from '../../../generated-common';
import { token } from '@atlaskit/tokens';

export const EditConfirm = styled.div`
  display: flex;
  align-items: center;
  margin-left: 10px;
`;
export const EditConfirmItem = styled.div`
  display: flex;
  align-items: center;
  margin-right: 10px;
`;
export const EditConfirmItem2 = styled.div`
  display: flex;
  align-items: center;
`;

const ReadViewContainer = styled.div`
  border-radius: 5px;
  display: flex;
  max-width: 100%;
  overflow: hidden;
  padding: 6px 8px;
  font-size: ${fontSize()};
  height: 20px;
  &:hover {
    background: ${token('elevation.surface.overlay.hovered', '#f4f5f7')};
  }
  cursor: default;
`;

const ReadViewContainerOuter = ({
  optionId,
  isDisabled,
  validationErrorFromServer,
  value,
  searchMatch,
  setIsEdit,
}: {
  optionId: string;
  isDisabled: boolean;
  validationErrorFromServer: OptionValidationErrorForView | null;
  value: string | null;
  searchMatch: OptionSearchMatchMarker;
  setIsEdit: (x: boolean) => void;
}) => (
  <div data-e2e-toggle-to-edit-parent="true">
    <ReadViewContainer onClick={() => (isDisabled ? null : setIsEdit(true))}>
      <div
        style={{
          cursor: isDisabled ? 'not-allowed' : 'pointer',
          textOverflow: 'ellipsis',
          overflow: 'hidden',
          whiteSpace: 'nowrap' /* WE USE THE min-width:0 APPROACH => https://css-tricks.com/flexbox-truncated-text/ */,
        }}
      >
        {/* SHOULD LOOK LIKE THIS https://user-images.githubusercontent.com/12599965/72605955-03932b00-391e-11ea-8869-cb92227c3d5d.gif */}
        {validationErrorFromServer !== null && validationErrorFromServer.optionId === optionId && validationErrorFromServer ? (
          <ErrorMessage>{validationErrorFromServer.errorMessage}</ErrorMessage>
        ) : (
          <span
            style={{
              color: isDisabled ? token('color.text.accent.gray') : token('color.text'),
            }}
          >
            <FieldEditorHighlightedOptionSearchMatch value={value} searchMatch={searchMatch} />
          </span>
        )}
        {/* TOOLTIP BREAKS THE ELIPSIS FOR LONG OPTIONS! SO WE LEAVE IT OUT */}
      </div>
    </ReadViewContainer>
  </div>
);

export const FieldEditorInPlaceEditOptionValue = ({
  option,
  isDisabled,
  validationErrorFromServer,
  onUpdatedOption,
  allOptionIdsAndValuesLowerCase,
  searchMatch,
}: {
  option: DTOCustomFieldOption | null;
  isDisabled: boolean;
  validationErrorFromServer: OptionValidationErrorForView | null;
  onUpdatedOption: (o: DTOCustomFieldOption) => Promise<FieldEditorActionReturnType>;
  allOptionIdsAndValuesLowerCase: IdAndValue[];
  searchMatch: OptionSearchMatchMarker;
}) => {
  const [value, setValue] = useState(option ? `${option.value}` : null);
  const [isEdit, setIsEdit] = useState(false);
  const [doConfirm, setDoConfirm] = useState(0);
  const [hasDuplicateError, setHasDuplicateError] = useState<boolean>(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const { t } = useContext(GlobalContext);

  useEffect(() => {
    if (isEdit) {
      if (inputRef && inputRef.current) {
        inputRef.current.focus();
      }
    }
  }, [isEdit]);

  useEffect(() => {
    /* THIS AVOIDS REACT ERRORS LIKE "SET STATE ON UNMOUNTED COMPONENT" */
    let isSubscribed = true;
    const doConfirmInner = async (optionId: string, optionVal: string) => {
      await onUpdatedOption({ id: optionId, value: optionVal, disabled: option ? option.disabled : false, optionId: option ? option.optionId : undefined });
      if (isSubscribed) {
        setIsEdit(false);
      }
    };
    if (doConfirm !== 0 && option && option.id && value !== null && hasDuplicateError !== true) {
      doConfirmInner(option.id, value);
    }
    return () => {
      // effect cleanup
      isSubscribed = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [doConfirm]);

  const validate = (value: string) => {
    const existingOption = allOptionIdsAndValuesLowerCase.find((ol) => ol.value === value.toLowerCase());
    if (option && existingOption && existingOption.id !== option.id) {
      setHasDuplicateError(true);
    } else {
      setHasDuplicateError(false);
    }
  };

  const doCancel = () => {
    setIsEdit(false);
    setValue(option ? option.value : null);
    setHasDuplicateError(false);
  };
  const handleOnChange = (e: ChangeEvent<HTMLInputElement>) => {
    validate(e.target.value);
    setValue(e.target.value);
  };
  const handleOnBlur = () => {
    doCancel();
  };
  const handleOnFocus = () => {};

  const editViewContainer = (
    <>
      <div style={{ flex: 1 }}>
        <Textfield
          ref={inputRef}
          data-e2e-edit-optionvalue="true"
          value={value as any}
          isInvalid={hasDuplicateError}
          onChange={handleOnChange}
          onBlur={handleOnBlur}
          onFocus={handleOnFocus}
          onKeyDown={(e: React.KeyboardEvent) => {
            if (e.key === 'Escape') {
              doCancel();
            } else if (e.key === 'Enter') {
              setDoConfirm((prev) => prev + 1);
            } else {
              if (hasDuplicateError === true) {
                setHasDuplicateError(false);
              }
            }
          }}
          elemAfterInput={
            hasDuplicateError && (
              <div
                style={{
                  paddingRight: `${gridSize() + 2}px`,
                  lineHeight: '100%',
                }}
              >
                <div style={{ color: token('color.text.danger', '#ff0000'), display: 'flex', alignItems: 'center' }}>
                  <span style={{ fontSize: '10px' }}>{t('fieldEditor.editOption.duplicateErrorShort')}</span>
                  <ErrorIcon label="error" primaryColor={token('color.text.danger', '#ff0000')} />
                </div>
              </div>
            )
          }
        />
      </div>
      <EditConfirm>
        <EditConfirmItem>
          <Tooltip content={t('fieldEditor.editConfirm.yes')}>
            <span data-e2e-edit-confirm-option-button-confirm={option ? option.id : '??'}>
              <Button
                appearance="default"
                onClick={() => setDoConfirm((prev) => prev + 1)}
                iconBefore={<CheckIcon label="confirm" primaryColor={token('color.text.accent.blue.bolder', '#253858')} />}
              />
            </span>
          </Tooltip>
        </EditConfirmItem>
        <EditConfirmItem2>
          <Tooltip content={t('fieldEditor.editConfirm.no')}>
            <span data-e2e-edit-confirm-option-button-cancel={option ? option.id : '??'}>
              <Button appearance="default" onClick={() => doCancel()} iconBefore={<CrossIcon label="cancel" primaryColor={token('color.text.accent.blue.bolder', '#253858')} />} />
            </span>
          </Tooltip>
        </EditConfirmItem2>
      </EditConfirm>
    </>
  );

  return (
    <div data-e2e-edit-inline-optionvalue={option ? option.id : '???'} style={{ marginLeft: '-8px', marginBottom: '4px', paddingRight: '20px' }}>
      <div style={{ display: !isEdit ? 'none' : 'flex', alignItems: 'center', height: '45px' }}>{editViewContainer}</div>
      <div style={{ display: !isEdit ? 'flex' : 'none', alignItems: 'center', height: '45px' }}>
        <div style={{ minWidth: 0, flex: 1 }}>
          {/* minWidth=0 is for text-overflow!!! */}
          <ReadViewContainerOuter
            optionId={option ? (option!.id ? option!.id : '-1') : '-1'}
            isDisabled={isDisabled}
            validationErrorFromServer={validationErrorFromServer}
            value={value}
            setIsEdit={setIsEdit}
            searchMatch={searchMatch}
          />
        </div>
      </div>
    </div>
  );
};
